import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Wallet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import JeaInput from "@/components/jea/JeaInput";
import JeaButton from "@/components/jea/JeaButton";
import { apiClient, formatApiError } from "@/lib/apiClient";

const EVENT_TYPES = [
  { key: "payment_received",  label: "Payment Received" },
  { key: "settlement",         label: "Settlement" },
  { key: "wage_garnishment",   label: "Wage Garnishment Payment" },
  { key: "bank_levy",          label: "Bank Levy Recovery" },
  { key: "other",              label: "Other" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const LogCollectionEventModal = ({ open, debtor, onClose, onLogged }) => {
  const [eventType, setEventType] = useState("payment_received");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayISO());
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setEventType("payment_received");
    setAmount("");
    setDate(todayISO());
    setNotes("");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const num = Number(amount);
    if (!Number.isFinite(num) || num <= 0) {
      toast.error("Enter a positive amount.");
      return;
    }
    if (!date) { toast.error("Pick a date."); return; }
    setSaving(true);
    try {
      const { data } = await apiClient.post(`/debtors/${debtor.id}/collection-events`, {
        event_type: eventType,
        amount_reported_cents: Math.round(num * 100),
        event_date: date,
        notes: notes.trim() || undefined,
      });
      toast.success("Collection event logged. Verifying against broker records…");
      onLogged?.(data);
      reset();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-[12px] z-[95]"
            onClick={saving ? undefined : onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="fixed inset-0 z-[96] flex items-center justify-center px-4 py-6"
            data-testid="log-collection-modal"
          >
            <div
              className="w-full max-w-[480px] max-h-[92vh] overflow-y-auto bg-bg-secondary border border-border-default rounded-[16px] p-8 shadow-elevated"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-[8px] bg-accent-pink/15 border border-accent-pink/40 flex items-center justify-center">
                    <Wallet size={18} className="text-accent-pink" />
                  </div>
                  <div>
                    <div className="jea-caption">{debtor?.full_name}</div>
                    <h2 className="text-white font-bold text-[20px]">Log a Collection Event</h2>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="p-2 text-text-secondary hover:text-white"
                  data-testid="log-collection-close"
                >
                  <X size={16} />
                </button>
              </div>

              <p className="text-text-secondary text-[13px] mb-5 leading-6">
                Recording payments received against this judgment helps track recovery. All entries
                are verified against broker data.
              </p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="jea-caption block mb-1.5" htmlFor="ce-type">Event Type</label>
                  <select
                    id="ce-type"
                    value={eventType}
                    onChange={(e) => setEventType(e.target.value)}
                    className="jea-focus w-full h-11 px-3 rounded-[8px] bg-bg-tertiary border border-border-default text-white text-[14px] focus:outline-none"
                    data-testid="log-collection-type"
                  >
                    {EVENT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="jea-caption block mb-1.5" htmlFor="ce-amount">Amount Received</label>
                  <JeaInput
                    id="ce-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    placeholder="0.00"
                    leftIcon={<span className="text-text-secondary">$</span>}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    data-testid="log-collection-amount"
                  />
                </div>
                <div>
                  <label className="jea-caption block mb-1.5" htmlFor="ce-date">Date Received</label>
                  <input
                    id="ce-date"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    max={todayISO()}
                    className="jea-focus w-full h-11 px-3 rounded-[8px] bg-bg-tertiary border border-border-default text-white text-[14px] focus:outline-none"
                    data-testid="log-collection-date"
                  />
                </div>
                <div>
                  <label className="jea-caption block mb-1.5" htmlFor="ce-notes">Notes (optional)</label>
                  <textarea
                    id="ce-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Wire ref, court order #, etc."
                    className="jea-focus w-full min-h-[80px] px-3 py-2 rounded-[8px] bg-bg-tertiary border border-border-default text-white text-[14px] focus:outline-none"
                    data-testid="log-collection-notes"
                  />
                </div>

                <div className="pt-2 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={saving}
                    className="h-11 px-5 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[14px] font-semibold transition-colors disabled:opacity-40"
                    data-testid="log-collection-cancel"
                  >
                    Cancel
                  </button>
                  <JeaButton type="submit" onClick={submit} loading={saving} className="gap-2" data-testid="log-collection-submit">
                    {saving ? (<><Loader2 size={14} className="animate-spin" /> Logging…</>) : "Log Event"}
                  </JeaButton>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default LogCollectionEventModal;
